import { get, post } from '../api.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { humanDuration } from '../utils.js';

function humanStorageYr(gbYr) {
  const abs = Math.abs(gbYr);
  let val, unit;
  if (abs >= 1)        { val = gbYr;              unit = 'GB'; }
  else if (abs >= 1/1024) { val = gbYr * 1024;       unit = 'MB'; }
  else                 { val = gbYr * 1024 * 1024; unit = 'KB'; }
  // Show 2 decimals, drop trailing zeros
  const formatted = parseFloat(val.toFixed(2));
  return `${formatted} ${unit}&middot;yr`;
}

export async function renderBilling(container) {
  setTopbar('Billing', '<button class="btn btn-primary btn-sm" id="topup-topbar-btn"><i data-lucide="plus" style="width:16px;height:16px"></i> Top Up</button>');

  container.innerHTML = '<p class="loading">Loading...</p>';

  try {
    const [balance, history] = await Promise.all([
      get('/billing/balance'),
      get('/billing/history'),
    ]);

    const gbBalance = balance.gb_year_balance;
    const burnGB = balance.burn_rate_gb;
    const reservedGBYr = balance.reserved_gb_years || 0;
    const availableGBYr = balance.available_gb_years != null ? balance.available_gb_years : gbBalance;
    const isNegative = availableGBYr < 0;
    const isInfinite = !burnGB || !isFinite(balance.estimated_years_remaining) || isNegative;

    // Gauge percentage based on total balance (available + reserved) vs capacity
    const totalCapacity = gbBalance + burnGB;
    const availablePct = isNegative ? 0 : (totalCapacity > 0 ? Math.min(100, Math.round((availableGBYr / totalCapacity) * 100)) : 0);
    const reservedPct = totalCapacity > 0 ? Math.min(100 - availablePct, Math.round((reservedGBYr / totalCapacity) * 100)) : 0;
    const pct = availablePct;
    const totalPct = availablePct + reservedPct;
    const circumference = 2 * Math.PI * 65;
    const offset = circumference * (1 - totalPct / 100);
    const reservedOffset = circumference * (1 - reservedPct / 100);

    const debt = isNegative ? Math.abs(availableGBYr) : 0;

    let countdownHtml = '&infin;';
    if (isNegative) {
      countdownHtml = 'Expired';
    } else if (!isInfinite) {
      countdownHtml = humanDuration(balance.estimated_hours_remaining * 3600);
    }

    // Convert history mbhours_added to GB·years for display
    const MBHOURS_PER_GB_YEAR = 1024 * 720 * 12;

    let html = `
      <div class="gauge-wrap">
        <div class="gauge">
          <svg width="160" height="160" viewBox="0 0 160 160">
            <circle class="track" cx="80" cy="80" r="65"/>
            <circle class="fill" cx="80" cy="80" r="65" stroke-dasharray="${circumference}" stroke-dashoffset="${circumference}" id="gauge-fill"/>
            ${reservedPct > 0 ? `<circle class="reserved" cx="80" cy="80" r="65" stroke-dasharray="${circumference}" stroke-dashoffset="${circumference}" id="gauge-reserved"/>` : ''}
          </svg>
          <div class="gauge-label">
            <span class="pct">${availablePct}%</span>
            <span class="sub">available</span>
          </div>
        </div>
        <div class="gauge-stats">
          <div class="stat">
            <div class="stat-value countdown" id="countdown">${countdownHtml}</div>
            <div class="stat-label">Time remaining at current usage</div>
          </div>
          <div class="stat">
            <div class="stat-value">${burnGB.toFixed(3)} <span style="font-size:14px;color:var(--text-secondary)">GB</span></div>
            <div class="stat-label">Current storage usage</div>
          </div>
        </div>
      </div>

      <div class="card" style="margin-bottom:20px">
        <div style="display:flex;justify-content:space-between;margin-bottom:4px">
          <span style="font-size:13px;color:var(--text-secondary)">Balance</span>
          <span class="mono" ${isNegative ? 'style="color:var(--danger)"' : ''}>${isNegative ? '&minus;' + humanStorageYr(debt) : humanStorageYr(gbBalance)}</span>
        </div>
        <div style="height:6px;background:var(--border);border-radius:3px;overflow:hidden;display:flex">
          <div style="width:${availablePct}%;height:100%;background:${isNegative ? 'var(--danger)' : 'var(--accent)'};transition:width 1s"></div>
          ${reservedPct > 0 ? `<div style="width:${reservedPct}%;height:100%;background:rgba(62,207,180,.3);transition:width 1s"></div>` : ''}
        </div>
        ${reservedGBYr > 0 ? `
        <div style="margin-top:6px;font-size:12px;color:var(--text-secondary);line-height:1.5">
          <span>Available: <span class="mono">${humanStorageYr(availableGBYr)}</span></span><br>
          <span>Reserved for will: <span class="mono">${humanStorageYr(reservedGBYr)}</span></span>
        </div>` : ''}
        ${balance.grace_expires_at
          ? `<div class="msg msg-error" style="margin-top:1rem">Grace period active — outstanding usage: ${humanStorageYr(debt)}. Data will be deleted on ${new Date(balance.grace_expires_at).toLocaleString()}.</div>`
          : ''}
      </div>

      <div class="section-title">Top-up History</div>
      <div class="card">
        ${history.length ? `
          <div class="table-wrap"><table>
            <thead><tr><th>Date</th><th>Credits</th><th>Payment ID</th></tr></thead>
            <tbody>
              ${history.map(h => `<tr>
                <td>${new Date(h.created_at).toLocaleDateString()}</td>
                <td class="mono">${humanStorageYr(h.mbhours_added / MBHOURS_PER_GB_YEAR)}</td>
                <td style="font-size:12px;color:var(--text-secondary)">${esc(h.stripe_payment_id || 'free')}</td>
              </tr>`).join('')}
            </tbody>
          </table></div>
        ` : '<div class="empty">No top-ups yet</div>'}
      </div>`;

    container.innerHTML = html;
    lucideIcons();

    // Animate gauge
    setTimeout(() => {
      const fill = document.getElementById('gauge-fill');
      if (fill) fill.style.strokeDashoffset = offset;
      const reserved = document.getElementById('gauge-reserved');
      if (reserved) reserved.style.strokeDashoffset = reservedOffset;
    }, 50);

    // Live countdown
    if (!isInfinite) {
      const targetTime = Date.now() + balance.estimated_hours_remaining * 3_600_000;
      const tickId = setInterval(() => {
        const el = document.getElementById('countdown');
        if (!el || !document.body.contains(el)) { clearInterval(tickId); return; }
        const remaining = targetTime - Date.now();
        if (remaining <= 0) { el.textContent = 'Expired'; clearInterval(tickId); return; }
        const s = Math.floor(remaining / 1000);
        el.textContent = humanDuration(s);
      }, 1000);
    }

    // Top up button (in topbar)
    const topupBtn = document.getElementById('topup-topbar-btn');
    if (topupBtn) {
      topupBtn.onclick = () => showTopupModal(debt);
    }
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
  }
}

function showTopupModal(debt = 0) {
  if (document.getElementById('topup-overlay')) return;

  const debtNotice = debt > 0
    ? `<div class="msg msg-error" style="margin-bottom:12px;font-size:13px">Outstanding usage of ${debt.toFixed(2)} GB&middot;mo will be deducted from your top-up.</div>`
    : '';

  const overlay = document.createElement('div');
  overlay.id = 'topup-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:1000;display:flex;align-items:center;justify-content:center';
  overlay.innerHTML = `
    <div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:24px;max-width:360px;width:90%">
      <h3 style="margin:0 0 16px">Top Up Storage</h3>
      ${debtNotice}
      <div style="display:flex;flex-direction:column;gap:12px">
        <button class="btn btn-primary" id="buy-1gb" style="justify-content:space-between;display:flex">
          <span>1 GB&middot;year</span><span>&euro;5.00</span>
        </button>
        <button class="btn btn-primary" id="buy-10gb" style="justify-content:space-between;display:flex">
          <span>10 GB&middot;years</span><span>&euro;25.00</span>
        </button>
      </div>
      <div id="topup-msg" style="margin-top:12px"></div>
      <div style="text-align:center;margin-top:16px">
        <button class="btn" id="topup-cancel" style="width:100%">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  document.getElementById('topup-cancel').onclick = () => overlay.remove();
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };

  document.getElementById('buy-1gb').onclick = () => doCheckout(1);
  document.getElementById('buy-10gb').onclick = () => doCheckout(10);
}

async function doCheckout(gbYears) {
  const msg = document.getElementById('topup-msg');
  const btns = document.querySelectorAll('#topup-overlay .btn');
  btns.forEach(b => b.disabled = true);
  msg.innerHTML = '<div class="msg">Redirecting to payment...</div>';
  try {
    const result = await post('/billing/checkout', { gb_years: gbYears });
    window.location.href = result.checkout_url;
  } catch (err) {
    msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
    btns.forEach(b => b.disabled = false);
  }
}


